import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { climbsService } from '../services/climbs';
import { climbLogsService } from '../services/climb-logs';
import { gymsService } from '../services/gyms';
import { competitionsService } from '../services/competitions';
import { ShareClimbEditor } from '../components/climbs/share/ShareClimbEditor';
import type { ShareNavigationState } from '../components/climbs/share/share-navigation';
import { climbTitle } from '../utils/climb-title';
import type { Climb } from '../types/climb';
import type { ClimbLog } from '../types/climb-log';
import type { Gym } from '../types/gym';
import type { Competition } from '../types/competition';

/**
 * The share dialog's advanced mode, `…/share/advance/new`: the climb laid over
 * a photo or a video from the phone, in a layout picked by swiping across it.
 * A page of its own rather than a step of the dialog, since placing things on a
 * picture wants the whole screen.
 */
export default function ShareClimbAdvancedPage() {
  const { gymSlug, climbSlug } = useParams<{ gymSlug: string; climbSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isCompetition = location.pathname.startsWith('/competitions/');
  const isCrag = location.pathname.startsWith('/crags/');
  const navState = (location.state as ShareNavigationState | null) ?? {};
  const presetVideoUrl = navState.presetVideoUrl ?? null;

  const [climb, setClimb] = useState<Climb | null>(null);
  const [climbLog, setClimbLog] = useState<ClimbLog | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gymSlug || !climbSlug) return;
    const fetchData = async () => {
      try {
        const [climbData, locationData] = await Promise.all([
          climbsService.getClimbBySlug(climbSlug),
          isCompetition
            ? competitionsService.getCompetitionBySlug(gymSlug)
            : gymsService.getGymBySlug(gymSlug),
        ]);
        setClimb(climbData);
        trackEvent('share_climb_viewed', { climb_slug: climbSlug, climb_type: climbData.type?.toLowerCase() });
        if (isCompetition) setCompetition(locationData as Competition);
        else setGym(locationData as Gym);

        climbLogsService.getClimbLog(climbSlug)
          .then(setClimbLog)
          .catch(() => {});
      } catch {
        // leave state null — error UI handled below
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [gymSlug, climbSlug, isCompetition]);

  const gymImageUrl = gym?.logo_small_url ?? competition?.logo_small_url;
  const locationName = gym?.name ?? competition?.name ?? gymSlug ?? '';
  const climbPath = location.pathname.replace(/\/share\/advance\/new\/?$/, '');
  const locationPath = isCompetition ? `/competitions/${gymSlug}` : isCrag ? `/crags/${gymSlug}` : `/gyms/${gymSlug}`;

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: climb ? [
        { label: locationName, onClick: () => navigate(locationPath) },
        { label: climbTitle(climb), onClick: () => navigate(climbPath) },
        { label: t('shareClimb.dialogTitle') },
      ] : [],
      loading,
    }),
    [climb?.name, locationName, locationPath, climbPath, loading, t]
  );

  // Back where the editor was opened from — or, reached by a link, the climb.
  const leaveEditor = () => {
    const openedInApp = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
    if (openedInApp) navigate(-1);
    else navigate(climbPath);
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="w-full aspect-[4/5] rounded-md" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!climb) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <p className="text-muted-foreground">{t('shareClimb.notFound', 'Climb or send not found')}</p>
      </div>
    );
  }

  return (
    // Edge to edge on a phone, and up against the navbar (main's padding taken
    // back): the photo or the video is the page.
    <div className="-mx-4 -mt-3 max-w-2xl sm:mx-auto">
      <ShareClimbEditor
        climb={climb}
        climbLog={climbLog}
        gymImageUrl={gymImageUrl}
        climbPath={climbPath}
        presetVideoUrl={presetVideoUrl}
        onExit={leaveEditor}
      />
    </div>
  );
}
